/* eslint-disable no-confusing-arrow */
import { takeLatest, call, put, select } from 'redux-saga/effects';

// Services
import { employeesSalesApi } from 'services/apiDigitalTicket/employeesSales';
import { employeesApi } from 'services/apiDigitalTicket/employees';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';
import { CONST_EMPLOYEES_OBJECT } from 'constants/employees';

import * as EmployeesSalesActions from './actions';
import { EmployeesSalesTypes } from './types';

function* listEmployees({
  payload,
}: ReturnType<typeof EmployeesSalesActions.listEmployeesRequest>): any {
  try {
    const { body, filtering } = payload;
    if (!filtering) {
      yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    }
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(employeesApi.get, {
      params: {
        transportOperatorId: transportOperator.id,
        _includes: 'resume',
        ...body,
        type: JSON.stringify([
          CONST_EMPLOYEES_OBJECT.TYPES_EMPLOYEES.DRIVER.KEY,
          CONST_EMPLOYEES_OBJECT.TYPES_EMPLOYEES.TICKET_COLLECTOR.KEY,
        ]),
      },
    });

    if (response.status === 200) {
      yield put(
        EmployeesSalesActions.listEmployeesSuccess({
          employeesList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('employeesSales - listEmployees - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(EmployeesSalesActions.listEmployeesFailure());
    yield put(DialogsActions.setClose());
  }
}

function* getEmployee({
  payload,
  meta,
}: ReturnType<typeof EmployeesSalesActions.getEmployeeRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(employeesApi.get, {
      params: {
        transportOperatorId: transportOperator.id,
        _includes: 'resume',
        ...payload,
      },
    });

    if (response.status === 200) {
      yield put(
        EmployeesSalesActions.getEmployeeSuccess({
          employeeData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('employeesSales - getEmployee - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(EmployeesSalesActions.getEmployeeFailure());
    yield put(DialogsActions.setClose());
    meta.promise.reject(error);
  }
}

function* filterEmployees({
  payload,
}: ReturnType<typeof EmployeesSalesActions.filterEmployeesRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(employeesApi.get, {
      params: {
        transportOperatorId: transportOperator.id,
        _includes: 'resume',
        ...payload,
        type: JSON.stringify([
          CONST_EMPLOYEES_OBJECT.TYPES_EMPLOYEES.DRIVER.KEY,
          CONST_EMPLOYEES_OBJECT.TYPES_EMPLOYEES.TICKET_COLLECTOR.KEY,
        ]),
      },
    });

    if (response.status === 200) {
      yield put(
        EmployeesSalesActions.filterEmployeesSuccess({
          employeesList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('employeesSales - filterEmployees - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(EmployeesSalesActions.filterEmployeesFailure());
    yield put(DialogsActions.setClose());
  }
}

function* getResume({
  payload,
  meta,
}: ReturnType<
  typeof EmployeesSalesActions.getEmployeeSalesResumeRequest
>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(employeesSalesApi.getResume, {
      params: {
        transportOperatorId: transportOperator.id,
        ...payload,
      },
    });

    if (response.status === 200) {
      yield put(
        EmployeesSalesActions.getEmployeeSalesResumeSuccess({
          ...response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('employeesSales - getEmployeeSalesResume - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(EmployeesSalesActions.getEmployeeSalesResumeFailure());
    yield put(DialogsActions.setClose());
    meta.promise.reject(error);
  }
}

function* listPayments({
  payload,
}: ReturnType<typeof EmployeesSalesActions.listPaymentsRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando repasses' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(employeesSalesApi.getPayments, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        EmployeesSalesActions.listPaymentsSuccess({
          paymentsList: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('employeesSales - listPayments - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(EmployeesSalesActions.listPaymentsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* listTransactions({
  payload,
}: ReturnType<typeof EmployeesSalesActions.listTransactionsRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Carregando transações' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(employeesSalesApi.getTransactions, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        EmployeesSalesActions.listTransactionsSuccess({
          transactionsList: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('employeesSales - listTransactions - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(EmployeesSalesActions.listTransactionsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* addPayment({
  payload,
}: ReturnType<typeof EmployeesSalesActions.addPaymentEmployeeRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Recebendo repasse' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(employeesSalesApi.addPayment, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(EmployeesSalesActions.addPaymentEmployeeSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Repasse recebido com sucesso',
        }),
      );
      yield put(EmployeesSalesActions.listEmployeesRequest({}));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('employeesSales - addPaymentEmployee - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(EmployeesSalesActions.addPaymentEmployeeFailure());
    yield put(DialogsActions.setClose());
  }
}

function* getTripResume({
  payload,
  meta,
}: ReturnType<
  typeof EmployeesSalesActions.getEmployeeSalesTripResumeRequest
>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(employeesSalesApi.getTripResume, {
      params: {
        transportOperatorId: transportOperator.id,
        ...payload,
      },
    });

    if (response?.status === 200) {
      yield put(
        EmployeesSalesActions.getEmployeeSalesTripResumeSuccess({
          csv: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
      meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('employeesSales - getEmployeeSalesTripResume - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(EmployeesSalesActions.getEmployeeSalesTripResumeFailure());
    yield put(DialogsActions.setClose());
    meta.promise.reject(error);
  }
}

// eslint-disable-next-line import/prefer-default-export
export const employeesSalesSagas = [
  takeLatest(EmployeesSalesTypes.LIST_EMPLOYEES_REQUEST, listEmployees),
  takeLatest(EmployeesSalesTypes.GET_EMPLOYEE_REQUEST, getEmployee),
  takeLatest(EmployeesSalesTypes.FILTER_EMPLOYEES_REQUEST, filterEmployees),
  takeLatest(EmployeesSalesTypes.GET_EMPLOYEE_SALES_RESUME_REQUEST, getResume),
  takeLatest(
    EmployeesSalesTypes.GET_EMPLOYEE_SALES_TRIP_RESUME_REQUEST,
    getTripResume,
  ),
  takeLatest(EmployeesSalesTypes.LIST_PAYMENTS_REQUEST, listPayments),
  takeLatest(EmployeesSalesTypes.LIST_TRANSACTIONS_REQUEST, listTransactions),
  takeLatest(EmployeesSalesTypes.ADD_PAYMENT_EMPLOYEE_REQUEST, addPayment),
];
